'use client';

import { clearFilters, useSolutionStore } from '@/features/products/store/solution';
import { FilterGroup, type Filters, type SelectOption, type StepValue } from '@/features/products/types';
import { stepValuesToFilters } from '@/features/products/utils/stepValuesToFilters';
import { useMediaQuery } from '@/hooks';
import storage from '@/services/storage';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import { Dialog } from 'primereact/dialog';
import { SelectButton, type SelectButtonProps } from 'primereact/selectbutton';
import { useEffect, useMemo, useState } from 'react';
import { use100vh } from 'react-div-100vh';

import Card from '../HelpWizard/Card';
import styles from './InitialWizard.module.scss';

const buttonTemplate = (option: SelectButtonProps) => {
  const optionValue: StepValue = option.value;
  return <Card key={optionValue.value} title={optionValue.value} tooltip={option.tooltip ?? ''} />;
};

const renderButtons = (values: StepValue[], addValues: (value: StepValue[]) => void, options: SelectOption[] = []) => (
  <SelectButton
    className={styles.selectButton}
    value={values}
    options={[...options].sort((a, b) => (a.value.value < b.value.value ? -1 : 1))}
    onChange={(e) => {
      addValues(e.value);
    }}
    itemTemplate={buttonTemplate}
    multiple
  />
);

interface InitialWizardProps {
  filtersValue: Filters[];
}

const InitialWizard = ({ filtersValue }: InitialWizardProps) => {
  const { isMobile } = useMediaQuery();
  const [values, setValues] = useState<StepValue[]>([]);
  const [wasVisited, setWasVisited] = useState(() => {
    const storedValue = storage.getItem<boolean>('wasVisited');
    return storedValue !== null;
  });
  const height = use100vh();

  const localStorageSet = () => {
    storage.setItem('wasVisited', true);
    setWasVisited(true);
  };

  const minHeight = useMemo(() => {
    if (!height) return '60vh';
    return isMobile ? height : height * 0.7;
  }, [height, isMobile]);

  const options = useMemo(() => {
    const industry = filtersValue.find((f) => f.group === FilterGroup.INDUSTRY);
    if (!industry) return [];
    return (
      industry.children?.map(({ id: parentId, label, values: childValue }) => ({
        id: parentId,
        label,
        value:
          childValue.length > 0
            ? {
                id: parentId,
                group: FilterGroup.INDUSTRY,
                value: label,
                isParent: true,
                values: childValue.map(({ id, value }) => ({
                  id,
                  group: FilterGroup.INDUSTRY_AREA,
                  value,
                  isParent: false,
                })),
              }
            : { id: parentId, group: FilterGroup.INDUSTRY, value: label, isParent: false },
      })) ?? []
    );
  }, [filtersValue]);

  const [prevWasVisited, setPrevWasVisited] = useState(wasVisited);
  if (wasVisited !== prevWasVisited) {
    setPrevWasVisited(wasVisited);
    if (!wasVisited) {
      setValues([]);
    }
  }

  useEffect(() => {
    if (!wasVisited) {
      clearFilters();
    }
  }, [wasVisited]);

  const filterProducts = () => {
    useSolutionStore.setState({ filters: stepValuesToFilters(values) });
    localStorageSet();
  };

  const renderFooter = () => (
    <div className={styles.footer}>
      <Button
        variant="outlined"
        endIcon={<CheckOutlined />}
        onClick={filterProducts}
        autoFocus
        disabled={values.length === 0}
      >
        Done
      </Button>
    </div>
  );

  return (
    <Dialog
      style={{ minHeight }}
      maximizable={!isMobile}
      visible={!wasVisited}
      className={styles.dialog}
      footer={renderFooter}
      onHide={localStorageSet}
    >
      <section className={styles.container}>
        <h2>What is your industry?</h2>
        <p>
          We have specific solutions for industries and we can find them more easily for you. Multiple industries can be
          chosen.
        </p>
        <div className={styles.cards}>{renderButtons(values, setValues, options)}</div>
      </section>
    </Dialog>
  );
};

export default InitialWizard;
